import React, { useState, useEffect } from "react";
import { Modal, Button, Space, Typography, Checkbox, Grid } from "antd";
import { RocketOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useGuide } from "../context/GuideContext";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * OnboardingWelcome - Welcome modal shown to first-time users
 * Offers option to start a guided tour or explore independently
 */
export const OnboardingWelcome: React.FC = () => {
  const { t } = useTranslation("guide");
  const screens = useBreakpoint();
  const {
    isFirstTimeUser,
    onboardingDismissed,
    dismissOnboarding,
    startTour,
    openHelpCenter,
  } = useGuide();

  const [visible, setVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isMobile = !screens.md;

  // Show modal for first-time users who haven't dismissed onboarding
  useEffect(() => {
    if (isFirstTimeUser && !onboardingDismissed) {
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        setVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isFirstTimeUser, onboardingDismissed]);

  const handleStartTour = () => {
    setVisible(false);
    if (dontShowAgain) {
      dismissOnboarding();
    }
    // Start with dashboard overview tour
    startTour("dashboard-overview");
  };

  const handleExploreLater = () => {
    setVisible(false);
    if (dontShowAgain) {
      dismissOnboarding();
    }
  };

  const handleViewAllGuides = () => {
    setVisible(false);
    if (dontShowAgain) {
      dismissOnboarding();
    }
    openHelpCenter();
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      centered
      width={isMobile ? "95%" : 480}
      styles={{
        body: { padding: isMobile ? "24px 16px" : "32px" },
      }}
    >
      <div style={{ textAlign: "center" }}>
        {/* Icon */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #1890ff 0%, #722ed1 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          <RocketOutlined style={{ fontSize: 36, color: "#fff" }} />
        </div>

        {/* Title */}
        <Title level={3} style={{ marginBottom: 8 }}>
          {t("onboarding.welcome.title")}
        </Title>

        {/* Subtitle */}
        <Text
          type="secondary"
          style={{ display: "block", marginBottom: 32, fontSize: 15 }}
        >
          {t("onboarding.welcome.subtitle")}
        </Text>

        {/* Action Buttons */}
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Button
            type="primary"
            size="large"
            block
            onClick={handleStartTour}
            style={{ height: 48 }}
          >
            {t("onboarding.welcome.startTour")}
          </Button>

          <Button size="large" block onClick={handleViewAllGuides}>
            {t("helpCenter.featureGuides")}
          </Button>

          <Button type="link" onClick={handleExploreLater}>
            {t("onboarding.welcome.exploreLater")}
          </Button>
        </Space>

        {/* Don't show again checkbox */}
        <div style={{ marginTop: 24 }}>
          <Checkbox
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          >
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("onboarding.welcome.dontShowAgain")}
            </Text>
          </Checkbox>
        </div>
      </div>
    </Modal>
  );
};
